import { type FC, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
// Import Types
import type { ProcessTemplateWithAiTemplate } from 'types';
// Import Enums
import { DeliveryMethodEnum, SubscriptionSectionEnum } from 'enums';
// Import Constants
import { BLOCKED_BY_SUBSCRIPTION_CLASSNAME, l, PARTNER_SETTINGS } from 'constants/common';
// Import Hooks
import { useHasPermission } from 'hooks';
// Import UI Components
import { Button, Icon, Label } from '@geneui/components';
// Import Components
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
import AITemplateBuilderModal from '../AITemplateBuilderModal';
// Import Styles
import styles from './AIAssistantBanner.module.scss';

interface Props {
    /** Channel/editor specific condition owned by the caller. */
    isEnabled: boolean;
    deliveryMethod: DeliveryMethodEnum;
    body: string;
    language: string;
    subject?: Nullable<string>;
    eventId?: number;
    /** Channels whose content is plain text (SMS) instead of an HTML template. */
    isPlainText?: boolean;
    /** Extracts the previewable html for channels whose body is an envelope (dynamic pop-up). */
    getPreviewHtml?: (body: string) => string;
    onApply: (template: ProcessTemplateWithAiTemplate) => void;
}

const AIAssistantBanner: FC<Props> = (props) => {
    const {
        isEnabled,
        deliveryMethod,
        body,
        language,
        subject = null,
        eventId,
        isPlainText = false,
        getPreviewHtml,
        onApply,
    } = props;
    const { t } = useTranslation();
    const { hasPartnerSettings, hasSubscriptionSection } = useHasPermission();

    const [isModalOpened, setIsModalOpened] = useState(false);

    const isBlockedBySubscription = !hasSubscriptionSection(SubscriptionSectionEnum.AiTemplateEditing);

    if (!isEnabled || !hasPartnerSettings(PARTNER_SETTINGS.IsAllowAiTemplateEditing)) {
        return null;
    }

    return (
        <>
            <BlockedBySubscriptionInfo isBlocked={isBlockedBySubscription} position="top">
                <div
                    className={classNames(styles.aiAssistantBanner, {
                        [BLOCKED_BY_SUBSCRIPTION_CLASSNAME]: isBlockedBySubscription,
                        'not-clickable': isBlockedBySubscription,
                    })}
                >
                    <div className={styles.aiAssistantBannerIcon}>
                        <Icon type="bc-icon-AI" />
                    </div>
                    <div className={styles.aiAssistantBannerContent}>
                        <Label className={styles.aiAssistantBannerTitle}>
                            {t(l.AIEnhancementsTemplateBannerTitle)}
                        </Label>
                        <Label className={styles.aiAssistantBannerDescription}>
                            {t(l.AIEnhancementsTemplateBannerDescription)}
                        </Label>
                    </div>
                    <Button
                        className={styles.aiAssistantBannerAction}
                        icon="bc-icon-arrow-forward"
                        disabled={isBlockedBySubscription}
                        onClick={() => setIsModalOpened(true)}
                    >
                        {t(l.TryAIAssistant)}
                    </Button>
                </div>
            </BlockedBySubscriptionInfo>
            <AITemplateBuilderModal
                isOpen={isModalOpened}
                onClose={() => setIsModalOpened(false)}
                onApply={onApply}
                deliveryMethod={deliveryMethod}
                subject={subject}
                body={body}
                language={language}
                eventId={eventId}
                isPlainText={isPlainText}
                getPreviewHtml={getPreviewHtml}
            />
        </>
    );
};

export default memo(AIAssistantBanner);
